import { includes, pipe } from '@fxts/core';
import { ClientError } from './error';
import { Files } from '../../entities/pool/public/files';
import axios from 'axios';

/**
 * 파일 업로드시 option url 없으면 기본 api 주소 '/@fileUpload/file' 으로 고정
 * app/all/api/file.js 에서 api url 확인 후 opt 사용 요망
 */
export interface FileUploadOption {
  url: string;
}

export const uploadFileWithAxios = async ({ url, formData }: { url: string; formData: FormData }) => {
  const { data } = await axios.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

export const filesUpload = async (
  input_element: HTMLInputElement,
  opt?: FileUploadOption,
): Promise<Files[]> => {
  const files = input_element.files as FileList;
  const formData = new FormData();
  [...files].forEach((file) => {
    formData.append('file', file);
  });
  return uploadFileWithAxios({ url: opt?.url ?? `/${ET.lang}/@fileUpload/file`, formData });
};

export const fileUpload = async (input_element: HTMLInputElement, opt?: FileUploadOption): Promise<Files> => {
  const files = await filesUpload(input_element, opt);
  if (files?.[0]) {
    return files[0];
  }
  throw new Error('It seems there is an issue with the file upload. Please try again.');
};

export interface ImageCheckOptions {
  /*
   * MB 단위
   * */
  max_file_volume?: number;
  image_size?: {
    width: number;
    height: number;
  };
  max_sizes?: {
    width: number;
    height: number;
  };
  accept_gif?: boolean;
  accepts?: ('jpg' | 'jpeg' | 'png' | 'webp' | 'gif')[];
  /*
   * image_size 가 존재해야 함
   * */
  check_aspect_ratio?: boolean;
}

// 이미지 비율 체크 함수
export const isAspectRatioCorrect = (
  current_width: number,
  current_height: number,
  expected_width: number,
  expected_height: number,
) => {
  const current_ratio = current_width / current_height;
  const expected_ratio = expected_width / expected_height;

  return Math.abs(current_ratio - expected_ratio) <= 0;
};

export const imageCheck = async (file: File, option?: ImageCheckOptions) => {
  const {
    max_file_volume,
    image_size,
    max_sizes,
    accept_gif,
    check_aspect_ratio = false,
    accepts = ['jpg', 'jpeg', 'png', 'webp'],
  } = option || {};

  const file_volume_over_error = {
    title: ET('mps2::file::volume_error_title'),
    message: ET('mps2::file::volume_error_message', {
      volume: `${max_file_volume}MB` || '',
    }),
  };

  const image_size_correct_error = {
    title: ET('mps2::file::image_size_error_title'),
    message: ET('mps2::file::image_size_error_message', {
      size: image_size ? `${image_size.width}px * ${image_size.height}px` : '',
    }),
  };

  const image_size_over_error = {
    title: ET('mps2::file::image_size_error_title'),
    message: ET('mps2::file::image_size_over_error_message', {
      size: max_sizes ? `${max_sizes.width}px * ${max_sizes.height}px` : '',
    }),
  };

  const not_allowed_extension_error = {
    title: ET('mps2::file::extension_error_title'),
    message: ET('mps2::file::extension_error_message'),
  };

  const extensions = accept_gif ? [...accepts, 'gif'] : accepts;

  return pipe(
    file,
    (file) =>
      new Promise<string | ArrayBuffer>((res, rej) => {
        if (max_file_volume && file.size > max_file_volume * 1024 * 1000) {
          return rej(new ClientError(file_volume_over_error));
        }

        if (!includes(file.type.split('/')[1], extensions)) {
          return rej(new ClientError(not_allowed_extension_error));
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function () {
          res(reader.result || '');
        };
      }),
    (src) =>
      new Promise<{ src: string | ArrayBuffer; ratio: number }>((res, rej) => {
        const img = new Image();
        img.src = src.toString();

        img.onload = async function () {
          if (image_size) {
            if (img.width < image_size.width || img.height < image_size.height) {
              return rej(new ClientError(image_size_correct_error));
            }

            if (check_aspect_ratio) {
              if (!isAspectRatioCorrect(img.width, img.height, image_size.width, image_size.height)) {
                return rej(new ClientError(image_size_correct_error));
              }
            }
          }

          if (max_sizes) {
            if (img.width > max_sizes.width || img.height > max_sizes.height) {
              return rej(new ClientError(image_size_over_error));
            }
          }

          res({ src, ratio: img.width / img.height });
        };
      }),
  );
};

export const ensureImageExtension = (filename: string): string => {
  const validExtensions = ['.jpeg', '.jpg', '.png', '.webp', '.gif'];

  const hasValidExtension = validExtensions.some((ext) => filename.toLowerCase().endsWith(ext));
  return hasValidExtension ? filename : `${filename}.png`;
};
